import * as THREE from 'three'
import { RAPIER } from '../physics'
import type { BspLoadedLevel } from './bsp-loader'
import { DestructibleManager } from '../world/destructibles'
import { colliderRegistry } from '../world/collider-registry'
import { GltfPropLoader } from './gltf-prop-loader'
import type { DebugMaterialRegistry, ObjectMeshType } from '../debug/debug-materials'
import propManifest from '../../assets/models/all_props_manifest.json'
import {
  createAuthenticWallMaterial,
  createAuthenticFloorMaterial,
  createAuthenticCeilingMaterial,
  getAuthenticMaterial,
} from './textures'

export interface PropManifestEntry {
  id: string
  sector?: string
  type: string
  tag: string
  sourceVertexCount: number
  exportedTriangleCount: number
  hullFellBackToBoundingBox?: boolean
  centroid: number[] | [number, number, number]
  vertices?: number[][] | Array<[number, number, number]>
  dimensions?: number[] | [number, number, number]
  dimensionsMeters?: number[] | [number, number, number]
  maxDimensionMeters?: number
  pedestalFitScale?: number
  texture?: string
  materialDescriptor?: Record<string, any>
  resolutionTier?: number
  resolutionRule?: string
  availableSwatches?: string[]
  file: string
}



/**
 * Resolve the canonical ObjectMeshType for a prop.
 *
 * Priority:
 *   1. `prop.type` (manifest field — authoritative)
 *   2. String heuristics on `prop.id` / `prop.tag` (fallback when type is absent or unrecognised)
 *
 * Type mapping:
 *   'door' | 'forcefield' → 'door'
 *   'breakable' | 'crate' | 'destructible' → 'destructible'
 *   'elevator' → 'elevator'
 *   everything else → 'wall'
 */
export function propObjectType(prop: PropManifestEntry): ObjectMeshType {
  // --- authoritative type from manifest ---
  if (prop.type === 'door' || prop.type === 'forcefield') return 'door'
  if (prop.type === 'breakable' || prop.type === 'crate' || prop.type === 'destructible') return 'destructible'
  if (prop.type === 'elevator') return 'elevator'

  // --- id / tag heuristics (fallback only) ---
  if (prop.id.startsWith('door_') || prop.id.includes('field')) return 'door'
  if (prop.id.startsWith('breakable') || prop.id.startsWith('crate')) return 'destructible'
  if (prop.id.startsWith('elevator_')) return 'elevator'
  if (prop.tag.includes('door') || prop.tag.includes('field')) return 'door'
  if (prop.tag.includes('crate') || prop.tag.includes('breakable')) return 'destructible'
  if (prop.tag.includes('elevator')) return 'elevator'

  return 'wall'
}

export async function buildGalleryLevel(
  scene: THREE.Scene,
  world: RAPIER.World,
  debugRegistry?: DebugMaterialRegistry,
): Promise<BspLoadedLevel> {
  const roomMeshes: THREE.Mesh[] = []
  const colliders: RAPIER.Collider[] = []
  const destructibleManager = new DestructibleManager(scene)

  const isBrowser = typeof window !== 'undefined'
  const urlParams = isBrowser ? new URLSearchParams(window.location.search) : null
  const biomeQuery = urlParams ? urlParams.get('biome')?.toLowerCase() : null

  let props: PropManifestEntry[] = propManifest as unknown as PropManifestEntry[]
  let galleryTitle = 'ESOTERIA 3D PROP GALLERY'

  if (biomeQuery) {
    if (biomeQuery === 'tunnel' || biomeQuery === 'tunnels' || biomeQuery === 'mt' || biomeQuery === 'mine') {
      props = props.filter((p) => p.sector?.startsWith('mt') || p.id.includes('mt') || p.texture?.includes('mine'))
      galleryTitle = 'MOUNTAIN & TUNNEL COMPLEX PAVILION'
    } else if (biomeQuery === 'base' || biomeQuery === 'bs' || biomeQuery === 'cbase') {
      props = props.filter((p) => p.sector?.startsWith('bs') || p.id.includes('bs') || p.texture?.includes('base'))
      galleryTitle = 'BASE STATION & AIRLOCK PAVILION'
    } else if (biomeQuery === 'q3') {
      props = props.filter((p) => p.sector?.startsWith('q3') || p.id.includes('q3'))
      galleryTitle = 'SECTOR Q3 RESEARCH FACILITY'
    } else if (biomeQuery === 'ab' || biomeQuery === 'alien') {
      props = props.filter((p) => p.sector?.startsWith('ab') || p.id.includes('ab'))
      galleryTitle = 'ALIEN BASE / AREA B PAVILION'
    } else if (biomeQuery === 'city') {
      props = props.filter((p) => p.sector?.startsWith('city') || p.id.includes('city'))
      galleryTitle = 'METRO CITY ARCHIVES'
    } else if (biomeQuery === 'fields' || biomeQuery === 'forcefield') {
      props = props.filter((p) => p.type === 'forcefield' || p.texture === 'elecfield1' || p.id.includes('field'))
      galleryTitle = 'PLASMA FORCEFIELD VAULT'
    } else if (biomeQuery === 'tier1') {
      props = props.filter((p) => p.resolutionTier === 1)
      galleryTitle = 'TIER-1 EXACT PROVENANCE PAVILION'
    }
  }

  // Grid layout parameters
  const cols = Math.min(6, Math.max(2, props.length))
  const spacingX = 5.5
  const spacingZ = 6.5
  const rows = Math.max(1, Math.ceil(props.length / cols))

  const hallWidth = cols * spacingX + 10.0
  const hallLength = rows * spacingZ + 24.0
  const hallHeight = 8.0

  const floorY = 0.0
  const ceilingY = hallHeight
  const centerZ = -(rows * spacingZ) / 2 - 5.0

  // 1. Floor & Ceiling
  const floorMat = createAuthenticFloorMaterial()
  const ceilingMat = createAuthenticCeilingMaterial()
  const wallMat = createAuthenticWallMaterial()

  const floorGeom = new THREE.PlaneGeometry(hallWidth, hallLength)
  floorGeom.rotateX(-Math.PI / 2)
  const floorMesh = new THREE.Mesh(floorGeom, floorMat)
  floorMesh.position.set(0, floorY, centerZ)
  scene.add(floorMesh)
  roomMeshes.push(floorMesh)

  // Rapier Floor Collider
  const floorBodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(0, floorY, centerZ)
  const floorBody = world.createRigidBody(floorBodyDesc)
  const floorColDesc = RAPIER.ColliderDesc.cuboid(hallWidth / 2, 0.1, hallLength / 2)
  const floorCollider = world.createCollider(floorColDesc, floorBody)
  colliders.push(floorCollider)

  // Ceiling
  const ceilingGeom = new THREE.PlaneGeometry(hallWidth, hallLength)
  ceilingGeom.rotateX(Math.PI / 2)
  const ceilingMesh = new THREE.Mesh(ceilingGeom, ceilingMat)
  ceilingMesh.position.set(0, ceilingY, centerZ)
  scene.add(ceilingMesh)
  roomMeshes.push(ceilingMesh)

  // Perimeter Walls
  const wallThickness = 0.5
  const wallDefs = [
    // Entrance Wall (Behind Spawn at +Z)
    { w: hallWidth, h: hallHeight, d: wallThickness, x: 0, y: hallHeight / 2, z: 6.0, name: 'Gallery Entrance Portal Wall' },
    // Far Exhibition End Wall (at -Z)
    { w: hallWidth, h: hallHeight, d: wallThickness, x: 0, y: hallHeight / 2, z: -(rows * spacingZ + 18.0), name: 'Gallery North Exhibition Wall' },
    // West Wall
    { w: wallThickness, h: hallHeight, d: hallLength, x: -hallWidth / 2, y: hallHeight / 2, z: centerZ, name: 'Gallery West Wing Wall' },
    // East Wall
    { w: wallThickness, h: hallHeight, d: hallLength, x: hallWidth / 2, y: hallHeight / 2, z: centerZ, name: 'Gallery East Wing Wall' },
  ]

  for (const w of wallDefs) {
    const geom = new THREE.BoxGeometry(w.w, w.h, w.d)
    const mesh = new THREE.Mesh(geom, wallMat)
    mesh.position.set(w.x, w.y, w.z)
    scene.add(mesh)
    roomMeshes.push(mesh)

    const bDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(w.x, w.y, w.z)
    const body = world.createRigidBody(bDesc)
    const cDesc = RAPIER.ColliderDesc.cuboid(w.w / 2, w.h / 2, w.d / 2)
    const col = world.createCollider(cDesc, body)
    colliders.push(col)

    colliderRegistry.register(col.handle, {
      id: w.name.toLowerCase().replace(/\s+/g, '_'),
      name: w.name,
      category: 'wall',
      sourceTag: 'ESOTERIA/GALLERY.E3 : Exhibition Wall',
    })

    if (debugRegistry) {
      debugRegistry.registerMesh(mesh, 'wall', wallMat, {
        id: w.name.toLowerCase().replace(/\s+/g, '_'),
        name: w.name,
        sourceTag: 'ESOTERIA/GALLERY.E3 : Exhibition Wall',
      })
    }
  }

  // 2. Exhibition Ambient & Directional Lighting
  const ambLight = new THREE.AmbientLight(0xddeeff, 2.5)
  scene.add(ambLight)

  const dirLight = new THREE.DirectionalLight(0xffffff, 2.0)
  dirLight.position.set(10, 20, 10)
  scene.add(dirLight)

  // 3. Pedestal and Accent Materials
  const pedestalMat = new THREE.MeshStandardMaterial({
    color: 0x16181f,
    roughness: 0.3,
    metalness: 0.7,
  })
  const neonCyanMat = new THREE.MeshBasicMaterial({ color: 0x00f0ff })
  const neonAmberMat = new THREE.MeshBasicMaterial({ color: 0xffaa00 })
  const neonMagentaMat = new THREE.MeshBasicMaterial({ color: 0xff007f })

  // 3b. Fast-path & concurrent pre-fetch via shared GltfPropLoader
  const propLoader = new GltfPropLoader(scene, world, destructibleManager, debugRegistry)
  const geometryCache = await propLoader.preloadGeometryBundle(
    '/assets/models/all_props.glb',
    props.map((p) => p.file),
  )

  const startX = -((cols - 1) * spacingX) / 2
  const startZ = -8.0 // First row starts 8m in front of player

  for (let i = 0; i < props.length; i++) {
    const prop = props[i]
    const c = i % cols
    const r = Math.floor(i / cols)

    const posX = startX + c * spacingX
    const posZ = startZ - r * spacingZ
    const pedestalH = 0.8
    const pedestalW = 2.0
    const pedestalD = 2.0

    // Pedestal Base Mesh
    const pedGeom = new THREE.BoxGeometry(pedestalW, pedestalH, pedestalD)
    const pedMesh = new THREE.Mesh(pedGeom, pedestalMat)
    pedMesh.position.set(posX, floorY + pedestalH / 2, posZ)
    scene.add(pedMesh)
    roomMeshes.push(pedMesh)

    // Canonical prop object type
    const objectType: ObjectMeshType = propObjectType(prop)

    // Glowing Neon Accent Base Rim
    const trimGeom = new THREE.BoxGeometry(pedestalW + 0.1, 0.06, pedestalD + 0.1)
    const trimMat = objectType === 'door' ? neonMagentaMat : objectType === 'destructible' ? neonAmberMat : neonCyanMat
    const trimMesh = new THREE.Mesh(trimGeom, trimMat)
    trimMesh.position.set(posX, floorY + 0.03, posZ)
    scene.add(trimMesh)

    // Pedestal Rapier Collider
    const pedBodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(posX, floorY + pedestalH / 2, posZ)
    const pedBody = world.createRigidBody(pedBodyDesc)
    const pedColDesc = RAPIER.ColliderDesc.cuboid(pedestalW / 2, pedestalH / 2, pedestalD / 2)
    const pedCol = world.createCollider(pedColDesc, pedBody)
    colliders.push(pedCol)

    // 4. Instantiation from pre-fetched pipeline geometry (instantaneous cache clone)
    const specimenMat = getAuthenticMaterial(prop.texture)
    const displayScale = prop.pedestalFitScale ?? 1.0
    const cachedGeom = geometryCache.get(prop.file)

    let specimenGeom: THREE.BufferGeometry
    if (cachedGeom) {
      specimenGeom = cachedGeom.clone()
    } else {
      const [w, h, d] = prop.dimensionsMeters ?? [1.0, 1.0, 1.0]
      specimenGeom = new THREE.BoxGeometry(Math.max(0.2, w), Math.max(0.2, h), Math.max(0.2, d))
    }

    if (displayScale !== 1.0) {
      specimenGeom.scale(displayScale, displayScale, displayScale)
    }
    specimenGeom.computeBoundingBox()

    const specimenMesh = new THREE.Mesh(specimenGeom, specimenMat)
    const finalMinY = specimenGeom.boundingBox ? specimenGeom.boundingBox.min.y : -0.4
    specimenMesh.position.set(posX, floorY + pedestalH - finalMinY + 0.04, posZ)

    specimenMesh.name = prop.id
    scene.add(specimenMesh)
    roomMeshes.push(specimenMesh)

    const dimM = prop.dimensionsMeters ?? [1.0, 1.0, 1.0]
    const meta = {
      id: prop.id,
      name: `${prop.id} (${objectType.toUpperCase()})`,
      sourceTag: `ESOTERIA/AREAS.E3 : ${prop.tag}`,
      dimensions: [dimM[0], dimM[1], dimM[2]] as [number, number, number],
      availableSwatches: prop.availableSwatches,
      getDetails: () => {
        const currentTex = (specimenMesh.userData?.currentTexture as string) || prop.texture || 'airpwall1'
        const swatches = prop.availableSwatches || []
        const tierStr = prop.resolutionTier
          ? `Tier ${prop.resolutionTier} (${prop.resolutionRule || 'resolved'})`
          : 'Tier 3 (Archetype Default)'
        return {
          'ID': prop.id,
          'Type': objectType.toUpperCase(),
          'Tag': prop.tag,
          'Dimensions': `${dimM[0]}m × ${dimM[1]}m × ${dimM[2]}m`,
          'Display Scale': displayScale === 1.0 ? '1:1 (Actual Scale)' : `${(displayScale * 100).toFixed(0)}% (Plinth Fit)`,
          'Texture': `${currentTex}.png`,
          'Provenance': tierStr,
          'Swatches': swatches.length > 1 ? `[KeyT: cycle ${swatches.length} variants]` : '1 variant',
          'Source Verts': prop.sourceVertexCount,
          'Triangles': prop.exportedTriangleCount,
          'GLTF Asset': prop.file,
        }
      },
    }

    colliderRegistry.register(pedCol.handle, {
      id: prop.id,
      name: `${prop.id} [${objectType.toUpperCase()}]`,
      category: objectType,
      sourceTag: prop.tag,
    })

    if (debugRegistry) {
      debugRegistry.registerMesh(
        specimenMesh,
        objectType,
        specimenMat,
        meta,
      )
    }
  }

  // Spawn position at gallery entrance looking down the central exhibition avenue (-Z)
  const spawnPosition = new THREE.Vector3(0, 1.2, 0.0)
  const facingRad = 0.0 // Naturally faces forward down -Z

  return {
    spawnPosition,
    facingRad,
    roomMeshes,
    doors: [],
    destructibles: [],
    destructibleManager,
    elevators: [],
    colliders,
    targets: [],
    messages: [
      {
        text: `${galleryTitle}: Displaying ${props.length} props. Aim at any pedestal to inspect provenance [WTHIT] or press KeyT to cycle authentic sector texture swatches.`,
        triggerObjectId: null,
      },
    ],
  }
}
